import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WeightChangeCategoryAdminControllerService } from '@app/generated/api/weightChangeCategoryAdminController.service';
import { WeightChangeCategoryControllerService } from '@app/generated/api/weightChangeCategoryController.service';
import { WeightChangeCategoryDto, CreateWeightChangeCategoryRequest } from '@app/generated/model';
import { ProjectAdministrationControllerService } from '@app/generated/api/projectAdministrationController.service';
import { ProjectDto } from '@admin/models/project-dto';

@Component({
  selector: 'app-weight-change-categories',
  templateUrl: './weight-change-categories.component.html',
  styleUrls: ['./weight-change-categories.component.scss'],
  standalone: false,
})
export class WeightChangeCategoriesComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  projects: ProjectDto[] = [];
  selectedProjectId: string | null = null;
  copyFromProjectId: string | null = null;
  categories: WeightChangeCategoryDto[] = [];
  loading = false;

  // Inline-add form state
  newLabel = '';
  addingNew = false;

  // Inline-edit state: tracks the id being edited and its draft values
  editingId: string | null = null;
  editLabel = '';
  editSortOrder: number | null = null;

  constructor(
    private adminCategoryService: WeightChangeCategoryAdminControllerService,
    private categoryService: WeightChangeCategoryControllerService,
    private projectAdminService: ProjectAdministrationControllerService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.projectAdminService
      .getProjects()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (projects) => (this.projects = (projects as any[]) ?? []),
        error: () => this.snackBar.open('Failed to load projects', 'OK', { duration: 4000 }),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onProjectChange(projectId: string): void {
    this.selectedProjectId = projectId;
    this.editingId = null;
    this.addingNew = false;
    this.loadCategories();
  }

  loadCategories(): void {
    if (!this.selectedProjectId) {
      return;
    }
    this.loading = true;
    this.adminCategoryService
      .listCategories(this.selectedProjectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cats) => {
          this.categories = this.sorted(cats ?? []);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.snackBar.open('Failed to load categories', 'OK', { duration: 4000 });
        },
      });
  }

  startAdd(): void {
    this.addingNew = true;
    this.newLabel = '';
    this.editingId = null;
  }

  cancelAdd(): void {
    this.addingNew = false;
    this.newLabel = '';
  }

  confirmAdd(): void {
    if (!this.selectedProjectId || !this.newLabel.trim()) {
      return;
    }
    const request: CreateWeightChangeCategoryRequest = {
      label: this.newLabel.trim(),
      sortOrder: this.categories.length + 1,
    };
    this.adminCategoryService.createCategory(this.selectedProjectId, request).subscribe({
      next: () => {
        this.addingNew = false;
        this.newLabel = '';
        this.loadCategories();
      },
      error: () => this.snackBar.open('Failed to add category', 'OK', { duration: 4000 }),
    });
  }

  startEdit(cat: WeightChangeCategoryDto): void {
    this.editingId = cat.id!;
    this.editLabel = cat.label ?? '';
    this.editSortOrder = cat.sortOrder ?? null;
    this.addingNew = false;
  }

  cancelEdit(): void {
    this.editingId = null;
  }

  confirmEdit(cat: WeightChangeCategoryDto): void {
    if (!this.selectedProjectId || !cat.id) {
      return;
    }
    this.adminCategoryService
      .updateCategory(this.selectedProjectId, cat.id, {
        label: this.editLabel.trim() || undefined,
        sortOrder: this.editSortOrder ?? undefined,
      })
      .subscribe({
        next: () => {
          this.editingId = null;
          this.loadCategories();
        },
        error: () => this.snackBar.open('Failed to update category', 'OK', { duration: 4000 }),
      });
  }

  deleteCategory(cat: WeightChangeCategoryDto): void {
    if (!this.selectedProjectId || !cat.id) {
      return;
    }
    this.adminCategoryService.deleteCategory(this.selectedProjectId, cat.id).subscribe({
      next: () => this.loadCategories(),
      error: () => this.snackBar.open('Failed to delete category', 'OK', { duration: 4000 }),
    });
  }

  copyFromProject(): void {
    if (!this.selectedProjectId || !this.copyFromProjectId || this.copyFromProjectId === this.selectedProjectId) {
      return;
    }
    // Read source categories then create each one in the target project.
    this.categoryService.getWeightChangeCategories(this.copyFromProjectId).subscribe({
      next: (sourceCats) => {
        if (!sourceCats?.length) {
          this.snackBar.open('Source project has no categories', 'OK', { duration: 3000 });
          return;
        }
        const creates = sourceCats.map((cat) =>
          this.adminCategoryService
            .createCategory(this.selectedProjectId!, { label: cat.label!, sortOrder: cat.sortOrder })
            .toPromise(),
        );
        Promise.all(creates).then(() => {
          this.copyFromProjectId = null;
          this.loadCategories();
          this.snackBar.open(`Copied ${creates.length} categories`, 'OK', { duration: 3000 });
        });
      },
      error: () => this.snackBar.open('Failed to load source project categories', 'OK', { duration: 4000 }),
    });
  }

  private sorted(cats: WeightChangeCategoryDto[]): WeightChangeCategoryDto[] {
    return [...cats].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }
}
